import React from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { AnalyticsFilters } from '../types/analytics.types';
import { useAdsAnalytics, AdsData } from '../hooks/useAnalytics';
import './AdsTab.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface AdsTabProps {
  filters: AnalyticsFilters;
}

export const AdsTab: React.FC<AdsTabProps> = ({ filters }) => {
  const { data, loading, error } = useAdsAnalytics(filters);

  if (error) {
    return (
      <div className="analytics-tab">
        <div className="error-message">
          <h4>Error Loading Ad Data</h4>
          <p>{error.message}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="analytics-tab ads-tab">
        <div className="loading-skeleton" style={{ height: '100px', marginBottom: '20px' }}></div>
        <div className="loading-skeleton" style={{ height: '300px', marginBottom: '20px' }}></div>
        <div className="loading-skeleton" style={{ height: '300px' }}></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="analytics-tab ads-tab">
        <div className="no-data-message">
          <h4>No Ad Data Available</h4>
          <p>Ad tracking data will appear here once users start watching ads in the app.</p>
        </div>
      </div>
    );
  }

  const { overview, daily, summary, users } = data;

  // Prepare daily chart data
  const dailyChartData = daily?.daily_stats ? {
    labels: daily.daily_stats.map(d => d.date),
    datasets: [
      {
        label: 'Total Ads Shown',
        data: daily.daily_stats.map(d => d.total_ads_shown),
        borderColor: '#4A90E2',
        backgroundColor: 'rgba(74, 144, 226, 0.1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      }
    ]
  } : null;

  const revenueChartData = daily?.daily_stats ? {
    labels: daily.daily_stats.map(d => d.date),
    datasets: [
      {
        label: 'Est. Revenue ($)',
        data: daily.daily_stats.map(d => d.estimated_revenue),
        borderColor: '#50C878',
        backgroundColor: 'rgba(80, 200, 120, 0.1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      }
    ]
  } : null;

  const adTypeChartData = daily?.daily_stats ? {
    labels: daily.daily_stats.map(d => d.date),
    datasets: [
      {
        label: 'Interstitial',
        data: daily.daily_stats.map(d => d.interstitial_shown),
        backgroundColor: '#4A90E2',
      },
      {
        label: 'Rewarded',
        data: daily.daily_stats.map(d => d.rewarded_shown),
        backgroundColor: '#9B59B6',
      }
    ]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
      },
      x: {
        grid: { display: false },
        ticks: { maxRotation: 45, minRotation: 45 }
      }
    }
  };

  return (
    <div className="analytics-tab ads-tab">
      {/* Summary Cards */}
      {summary && (
        <div className="ads-summary-cards">
          <div className="ads-summary-card">
            <span className="ads-card-value">{summary.total_ads_shown.toLocaleString()}</span>
            <span className="ads-card-label">Total Ads Shown</span>
          </div>
          <div className="ads-summary-card revenue">
            <span className="ads-card-value">${summary.total_estimated_revenue.toFixed(2)}</span>
            <span className="ads-card-label">Est. Revenue</span>
          </div>
          <div className="ads-summary-card">
            <span className="ads-card-value">{summary.unique_users.toLocaleString()}</span>
            <span className="ads-card-label">Users with Ads</span>
          </div>
          <div className="ads-summary-card">
            <span className="ads-card-value">{summary.avg_ads_per_user.toFixed(1)}</span>
            <span className="ads-card-label">Avg Ads/User</span>
          </div>
          <div className="ads-summary-card">
            <span className="ads-card-value">${summary.avg_revenue_per_user.toFixed(4)}</span>
            <span className="ads-card-label">Avg Rev/User</span>
          </div>
        </div>
      )}

      {/* Ad Type Breakdown */}
      {overview && (
        <div className="ads-type-breakdown">
          <div className="ads-type-card interstitial">
            <h4>Interstitial Ads</h4>
            <div className="ads-type-stats">
              <div className="ads-stat">
                <span className="ads-stat-value">{overview.interstitial.shown}</span>
                <span className="ads-stat-label">Shown</span>
              </div>
              <div className="ads-stat">
                <span className="ads-stat-value">{overview.interstitial.completion_rate}%</span>
                <span className="ads-stat-label">Completion</span>
              </div>
              <div className="ads-stat">
                <span className="ads-stat-value">${overview.interstitial.estimated_revenue.toFixed(2)}</span>
                <span className="ads-stat-label">Revenue</span>
              </div>
            </div>
          </div>

          <div className="ads-type-card rewarded">
            <h4>Rewarded Ads</h4>
            <div className="ads-type-stats">
              <div className="ads-stat">
                <span className="ads-stat-value">{overview.rewarded.shown}</span>
                <span className="ads-stat-label">Shown</span>
              </div>
              <div className="ads-stat">
                <span className="ads-stat-value">{overview.rewarded.completed}</span>
                <span className="ads-stat-label">Completed</span>
              </div>
              <div className="ads-stat">
                <span className="ads-stat-value">{overview.rewarded.completion_rate}%</span>
                <span className="ads-stat-label">Completion</span>
              </div>
              <div className="ads-stat">
                <span className="ads-stat-value">{overview.rewarded.total_lives_earned}</span>
                <span className="ads-stat-label">Lives Earned</span>
              </div>
              <div className="ads-stat">
                <span className="ads-stat-value">${overview.rewarded.estimated_revenue.toFixed(2)}</span>
                <span className="ads-stat-label">Revenue</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="ads-charts-grid">
        <div className="ads-chart-container">
          <div className="chart-header">
            <h3>Daily Ads Shown</h3>
          </div>
          {dailyChartData ? (
            <div className="chart-wrapper" style={{ height: '300px' }}>
              <Line data={dailyChartData} options={chartOptions} />
            </div>
          ) : (
            <p className="no-data">No daily data available</p>
          )}
        </div>

        <div className="ads-chart-container">
          <div className="chart-header">
            <h3>Daily Estimated Revenue</h3>
          </div>
          {revenueChartData ? (
            <div className="chart-wrapper" style={{ height: '300px' }}>
              <Line data={revenueChartData} options={chartOptions} />
            </div>
          ) : (
            <p className="no-data">No revenue data available</p>
          )}
        </div>

        <div className="ads-chart-container">
          <div className="chart-header">
            <h3>Ad Types by Day</h3>
          </div>
          {adTypeChartData ? (
            <div className="chart-wrapper" style={{ height: '300px' }}>
              <Bar data={adTypeChartData} options={{ ...chartOptions, plugins: { ...chartOptions.plugins, legend: { display: true } } }} />
            </div>
          ) : (
            <p className="no-data">No ad type data available</p>
          )}
        </div>
      </div>

      {/* Top Users by Ads */}
      {users && users.users.length > 0 && (
        <div className="ads-users-section">
          <h3>Top Users by Ad Views</h3>
          <div className="ads-users-table-container">
            <table className="ads-users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Total Ads</th>
                  <th>Interstitial</th>
                  <th>Rewarded</th>
                  <th>Completed</th>
                  <th>Failed</th>
                  <th>Lives Earned</th>
                  <th>Est. Revenue</th>
                </tr>
              </thead>
              <tbody>
                {users.users.slice(0, 20).map((user, index) => (
                  <tr key={user.user_id}>
                    <td className="user-cell">
                      <span className="user-rank">{index + 1}</span>
                      <span className="user-id" title={user.user_id}>
                        {user.user_id.length > 25 ? `${user.user_id.substring(0, 25)}...` : user.user_id}
                      </span>
                    </td>
                    <td>{user.total_ads}</td>
                    <td>{user.interstitial_shown}</td>
                    <td>{user.rewarded_shown}</td>
                    <td>{user.rewarded_completed}</td>
                    <td className={user.total_failed > 0 ? 'failed-cell' : ''}>{user.total_failed}</td>
                    <td>{user.lives_earned}</td>
                    <td className="revenue-cell">${user.estimated_revenue.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Platform Breakdown */}
      {overview && overview.by_platform.length > 0 && (
        <div className="ads-platform-section">
          <h3>Platform Breakdown</h3>
          <div className="platform-cards">
            {overview.by_platform.map(platform => (
              <div key={platform.platform} className="platform-card">
                <h4>{platform.platform === 'ios' ? 'iOS' : platform.platform === 'android' ? 'Android' : platform.platform}</h4>
                <div className="platform-stats">
                  <div className="platform-stat">
                    <span className="stat-value">{platform.interstitial_shown}</span>
                    <span className="stat-label">Interstitial</span>
                  </div>
                  <div className="platform-stat">
                    <span className="stat-value">{platform.rewarded_shown}</span>
                    <span className="stat-label">Rewarded</span>
                  </div>
                  <div className="platform-stat">
                    <span className="stat-value">{platform.rewarded_completed}</span>
                    <span className="stat-label">Completed</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
